import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getDocumentsPath } from '@/lib/osVFSBridge';
import { globalVFS } from '../../utils/storage/VFS';
import { ensureDir, FileSystemItem } from '../../utils/filesystem';
import { useAuth } from '../../hooks/useAuth';
import useOsDark from '../../hooks/useOsDark';
import { makeProgramIcon } from '../AppIcon';
import { DraggableFileDialog } from '../DraggableFileDialog';
import { useToast } from '../ui/use-toast';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from '../ui/menubar';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Textarea } from '../ui/textarea';
import {
  ArrowLeftRight,
  BarChart3,
  Bell,
  Building2,
  Calendar,
  CircleHelp,
  Cloud,
  DollarSign,
  FileSpreadsheet,
  FileText,
  Home,
  Info,
  Landmark,
  Monitor,
  Plus,
  Receipt,
  RefreshCcw,
  Save,
  TrendingUp,
  Upload,
  Wallet,
} from 'lucide-react';

type SaveMode = 'local' | 'cloud';
type SectionId = 'overview' | 'accounts' | 'register' | 'bills' | 'budget' | 'investing' | 'business' | 'reports';
type AccountType = 'checking' | 'savings' | 'credit' | 'investment' | 'loan' | 'mortgage' | 'asset' | 'liability' | 'receivable';
type TransactionType = 'deposit' | 'payment' | 'transfer';
type Frequency = 'weekly' | 'monthly' | 'quarterly' | 'yearly';
type InvoiceStatus = 'draft' | 'sent' | 'paid';

interface Account {
  id: string;
  name: string;
  type: AccountType;
  institution: string;
  balance: number;
  notes?: string;
}

interface MoneyTransaction {
  id: string;
  date: string;
  type: TransactionType;
  payee: string;
  memo: string;
  category: string;
  accountId: string;
  transferAccountId?: string;
  amount: number;
  cleared: boolean;
}

interface Bill {
  id: string;
  payee: string;
  amount: number;
  dueDate: string;
  frequency: Frequency;
  category: string;
  accountId: string;
  reminder: boolean;
  autoPay: boolean;
  active: boolean;
  lastPaidDate?: string;
}

interface BudgetItem {
  id: string;
  group: string;
  category: string;
  planned: number;
}

interface InvestmentHolding {
  id: string;
  accountId: string;
  symbol: string;
  name: string;
  shares: number;
  costBasis: number;
  currentPrice: number;
}

interface InvoiceItem {
  description: string;
  quantity: number;
  rate: number;
}

interface Invoice {
  id: string;
  customer: string;
  issueDate: string;
  dueDate: string;
  accountId: string;
  status: InvoiceStatus;
  items: InvoiceItem[];
  notes: string;
  paymentDate?: string;
}

interface MoneyBookData {
  version: 1;
  bookName: string;
  saveMode: SaveMode;
  localFilePath?: string;
  cloudBookId?: string;
  accounts: Account[];
  transactions: MoneyTransaction[];
  bills: Bill[];
  budgetItems: BudgetItem[];
  investments: InvestmentHolding[];
  invoices: Invoice[];
}

interface CloudBookSummary {
  id: string;
  book_name: string;
  updated_at?: string;
}

interface MapleMoneyProps {
  filePath?: string;
  initialContent?: string;
}

const createId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const today = () => new Date().toISOString().slice(0, 10);

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);

const formatDate = (value?: string) => {
  if (!value) return '—';
  return new Date(`${value}T00:00:00`).toLocaleDateString();
};

const sanitizeFileName = (value: string) =>
  value.replace(/[<>:"/\\|?*]+/g, '').trim() || 'MapleMoney-Book';

const addFrequency = (dateString: string, frequency: Frequency) => {
  const date = new Date(`${dateString}T00:00:00`);
  if (frequency === 'weekly') date.setDate(date.getDate() + 7);
  if (frequency === 'monthly') date.setMonth(date.getMonth() + 1);
  if (frequency === 'quarterly') date.setMonth(date.getMonth() + 3);
  if (frequency === 'yearly') date.setFullYear(date.getFullYear() + 1);
  return date.toISOString().slice(0, 10);
};

const daysUntil = (dateString: string) => {
  const current = new Date(`${today()}T00:00:00`).getTime();
  const target = new Date(`${dateString}T00:00:00`).getTime();
  return Math.round((target - current) / (1000 * 60 * 60 * 24));
};

const getInvoiceTotal = (invoice: Invoice) =>
  invoice.items.reduce((sum, item) => sum + item.quantity * item.rate, 0);

const starterBook = (): MoneyBookData => ({
  version: 1,
  bookName: 'MapleMoney Book',
  saveMode: 'local',
  accounts: [
    { id: 'acct-checking', name: 'Checking', type: 'checking', institution: 'Maple Bank', balance: 3255.22 },
    { id: 'acct-receivable', name: 'Accounts Receivable', type: 'receivable', institution: 'Business Ledger', balance: 3959.9 },
    { id: 'acct-sales-tax', name: 'Sales Tax', type: 'liability', institution: 'Tax Agency', balance: -0 },
    { id: 'acct-watch', name: 'Investments to Watch', type: 'investment', institution: 'Maple Brokerage', balance: 0 },
    { id: 'acct-savings', name: 'Emergency Savings', type: 'savings', institution: 'Maple Bank', balance: 10000 },
  ],
  transactions: [
    {
      id: 'txn-1',
      date: today(),
      type: 'deposit',
      payee: 'Opening Balance',
      memo: 'Initial checking balance',
      category: 'Starting Balance',
      accountId: 'acct-checking',
      amount: 3255.22,
      cleared: true,
    },
    {
      id: 'txn-2',
      date: today(),
      type: 'deposit',
      payee: 'Payroll',
      memo: 'Salary deposit',
      category: 'Income',
      accountId: 'acct-checking',
      amount: 2522,
      cleared: true,
    },
    {
      id: 'txn-3',
      date: today(),
      type: 'payment',
      payee: 'Groceries',
      memo: 'Weekly household shop',
      category: 'Food & Dining',
      accountId: 'acct-checking',
      amount: 184.45,
      cleared: true,
    },
  ],
  bills: [
    {
      id: 'bill-1',
      payee: 'Mortgage Payment',
      amount: 1850,
      dueDate: addFrequency(today(), 'monthly'),
      frequency: 'monthly',
      category: 'Housing',
      accountId: 'acct-checking',
      reminder: true,
      autoPay: false,
      active: true,
    },
    {
      id: 'bill-2',
      payee: 'Electric Bill',
      amount: 120,
      dueDate: addFrequency(today(), 'monthly'),
      frequency: 'monthly',
      category: 'Utilities',
      accountId: 'acct-checking',
      reminder: true,
      autoPay: false,
      active: true,
    },
  ],
  budgetItems: [
    { id: 'budget-1', group: 'Income', category: 'Income', planned: 2522 },
    { id: 'budget-2', group: 'Committed Expenses', category: 'Housing', planned: 1513.2 },
    { id: 'budget-3', group: 'Fun', category: 'Entertainment', planned: 252.2 },
    { id: 'budget-4', group: 'Irregular Expenses', category: 'Food & Dining', planned: 252.2 },
    { id: 'budget-5', group: 'Savings & Debt', category: 'Savings', planned: 252.2 },
    { id: 'budget-6', group: 'Retirement', category: 'Investments', planned: 252.2 },
  ],
  investments: [
    { id: 'inv-1', accountId: 'acct-watch', symbol: 'AAPL', name: 'Apple Inc.', shares: 50, costBasis: 150, currentPrice: 175.5 },
    { id: 'inv-2', accountId: 'acct-watch', symbol: 'MSFT', name: 'Microsoft Corp.', shares: 30, costBasis: 300, currentPrice: 340.75 },
    { id: 'inv-3', accountId: 'acct-watch', symbol: 'VOO', name: 'Vanguard S&P 500 ETF', shares: 100, costBasis: 350, currentPrice: 385.2 },
  ],
  invoices: [
    {
      id: 'invoice-1',
      customer: 'Christopher Robins',
      issueDate: today(),
      dueDate: addFrequency(today(), 'monthly'),
      accountId: 'acct-checking',
      status: 'sent',
      notes: 'Accounts receivable example invoice.',
      items: [{ description: 'Stuff', quantity: 10, rate: 395.99 }],
    },
  ],
});

const parseBook = (raw: string): MoneyBookData => {
  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Invalid MapleMoney file.');
  }

  return {
    version: 1,
    bookName: String(parsed.bookName || 'MapleMoney Book'),
    saveMode: parsed.saveMode === 'cloud' ? 'cloud' : 'local',
    localFilePath: parsed.localFilePath || undefined,
    cloudBookId: parsed.cloudBookId || undefined,
    accounts: Array.isArray(parsed.accounts) ? parsed.accounts : [],
    transactions: Array.isArray(parsed.transactions) ? parsed.transactions : [],
    bills: Array.isArray(parsed.bills) ? parsed.bills : [],
    budgetItems: Array.isArray(parsed.budgetItems) ? parsed.budgetItems : [],
    investments: Array.isArray(parsed.investments) ? parsed.investments : [],
    invoices: Array.isArray(parsed.invoices) ? parsed.invoices : [],
  } as MoneyBookData;
};

const getThemeClasses = (isDark: boolean) => ({
  panelClass: isDark
    ? 'border border-[#314b73] bg-[#17243a] shadow-none'
    : 'border border-[#97b8e5] bg-[#edf4ff] shadow-none',
  panelHeaderClass: isDark
    ? 'border-b border-[#314b73] bg-gradient-to-r from-[#243c5d] via-[#1f3553] to-[#28466b] py-2'
    : 'border-b border-[#97b8e5] bg-gradient-to-r from-[#cfe4ff] via-[#e4f0ff] to-[#c6dcfb] py-2',
  surfaceClass: isDark
    ? 'rounded border border-[#2b4365] bg-[#0f1a2d]'
    : 'rounded border border-[#c3d8f5] bg-white',
  surfaceSoftClass: isDark
    ? 'rounded border border-[#2b4365] bg-[#111d31]/95'
    : 'rounded border border-[#c3d8f5] bg-white/80',
  titleClass: isDark ? 'text-[#e5efff]' : 'text-[#17315a]',
  headingClass: isDark ? 'text-[#eff4ff]' : 'text-[#21406f]',
  mutedClass: isDark ? 'text-slate-300' : 'text-slate-600',
  topButtonClass: isDark
    ? 'border-white/15 bg-white/5 text-white hover:bg-white/10'
    : 'border-white/30 bg-white/10 text-white hover:bg-white/20',
  primaryButtonClass: isDark
    ? 'bg-[#2f63ba] text-white hover:bg-[#3f74cd]'
    : 'bg-[#2756a3] text-white hover:bg-[#214a8c]',
  topPrimaryButtonClass: isDark
    ? 'bg-[#285189] text-white hover:bg-[#3264a7]'
    : 'bg-[#234879] text-white hover:bg-[#1d3d67]',
  inputClass: isDark
    ? 'border-[#36547f] bg-[#0f1a2d] text-[#e5efff] placeholder:text-slate-500 hover:border-[#4d6ea3] focus-visible:border-[#5a86c9] focus-visible:ring-2 focus-visible:ring-[#35598b] focus-visible:ring-offset-0'
    : 'border-[#7fa8df] bg-[#fdfefe] shadow-[inset_0_1px_0_rgba(255,255,255,0.98),0_1px_2px_rgba(38,73,130,0.06)] text-[#17315a] placeholder:text-slate-400 hover:border-[#6f9bda] focus-visible:border-[#4f84d1] focus-visible:ring-2 focus-visible:ring-[#9cc0f2] focus-visible:ring-offset-0',
  topInputClass: isDark
    ? 'h-8 w-[220px] border-white/15 bg-white/10 text-white placeholder:text-white/60'
    : 'h-8 w-[220px] border-white/30 bg-white/15 text-white placeholder:text-white/60',
  badgeClass: isDark
    ? 'border-[#3b5f94] bg-[#13243c] text-[#e5efff]'
    : 'bg-white',
  topBadgeClass: isDark
    ? 'border-white/15 bg-white/8 text-white hover:bg-white/8'
    : 'border-white/30 bg-white/15 text-white hover:bg-white/15',
  activeNavItemClass: isDark
    ? 'bg-[#162640] font-semibold text-[#eff4ff] shadow-sm'
    : 'bg-white font-semibold text-[#17315a] shadow-sm',
  inactiveNavItemClass: isDark
    ? 'text-slate-300 hover:bg-[#182841]'
    : 'text-slate-700 hover:bg-white/70',
  positiveClass: isDark ? 'text-green-400' : 'text-green-600',
  negativeClass: isDark ? 'text-red-400' : 'text-red-600',
  accentClass: isDark ? 'text-[#e5efff]' : 'text-[#17315a]',
  tableRowClass: isDark ? 'border-[#2b4365]' : 'border-[#c3d8f5]',
  selectContentClass: isDark
    ? 'border-[#36547f] bg-[#111d31] text-[#e5efff]'
    : '',
  emptyStateClass: isDark
    ? 'rounded border border-dashed border-[#35527c] bg-[#0f1a2d]/70 p-4 text-sm text-slate-400'
    : 'rounded border border-dashed border-[#97b8e5] bg-white/60 p-4 text-sm text-slate-500',
  appRootClass: isDark
    ? 'flex h-full min-h-0 flex-col overflow-hidden bg-[#0f1726] text-[#dbe7ff]'
    : 'flex h-full min-h-0 flex-col overflow-hidden bg-[#eef3fb] text-[#17315a]',
  topBarClass: isDark
    ? 'border-b border-[#29456c] bg-gradient-to-r from-[#17345d] via-[#285182] to-[#18365f] px-4 py-2 text-white'
    : 'border-b border-[#6f96d3] bg-gradient-to-r from-[#4d86d2] via-[#75a6e2] to-[#4c8ad8] px-4 py-2 text-white',
  secondBarClass: isDark
    ? 'border-b border-[#28456d] bg-[#183765] px-4 py-2 text-sm text-white'
    : 'border-b border-[#335fa7] bg-[#2f57a0] px-4 py-2 text-sm text-white',
  sidebarClass: isDark
    ? 'w-[230px] shrink-0 border-r border-[#28415f] bg-[#121d31]'
    : 'w-[230px] shrink-0 border-r border-[#b8cdec] bg-[#e0ecfb]',
  sidebarHeaderClass: isDark
    ? 'border-b border-[#28415f] px-4 py-4'
    : 'border-b border-[#b8cdec] px-4 py-4',
  sidebarSeparatorClass: isDark ? 'bg-[#28415f]' : 'bg-[#b8cdec]',
  cloudPanelClass: isDark
    ? 'border-b border-[#28415f] bg-[#14243a] px-4 py-3'
    : 'border-b border-[#b8cdec] bg-[#d9e8fb] px-4 py-3',
  paidBadgeClass: isDark ? 'bg-green-600 text-white' : 'bg-green-600 text-white',
});

const MapleMoneyIcon = makeProgramIcon('maple-money', 'money');

export const MapleMoney: React.FC<MapleMoneyProps> = ({ filePath, initialContent }) => {
  const { user } = useAuth();
  const isDark = useOsDark();
  const { toast } = useToast();
  const {
    panelClass,
    panelHeaderClass,
    surfaceClass,
    surfaceSoftClass,
    titleClass,
    headingClass,
    mutedClass,
    topButtonClass,
    primaryButtonClass,
    topPrimaryButtonClass,
    inputClass,
    topInputClass,
    badgeClass,
    topBadgeClass,
    activeNavItemClass,
    inactiveNavItemClass,
    positiveClass,
    negativeClass,
    accentClass,
    tableRowClass,
    selectContentClass,
    emptyStateClass,
    appRootClass,
    topBarClass,
    secondBarClass,
    sidebarClass,
    sidebarHeaderClass,
    sidebarSeparatorClass,
    cloudPanelClass,
    paidBadgeClass,
  } = getThemeClasses(isDark);

  const [activeSection, setActiveSection] = useState<SectionId>('overview');

  const [bookName, setBookName] = useState(starterBook().bookName);
  const [saveMode, setSaveMode] = useState<SaveMode>('local');
  const [localFilePath, setLocalFilePath] = useState<string>('');
  const [cloudBookId, setCloudBookId] = useState<string>('');
  const [accounts, setAccounts] = useState<Account[]>(starterBook().accounts);
  const [transactions, setTransactions] = useState<MoneyTransaction[]>(starterBook().transactions);
  const [bills, setBills] = useState<Bill[]>(starterBook().bills);
  const [budgetItems, setBudgetItems] = useState<BudgetItem[]>(starterBook().budgetItems);
  const [investments, setInvestments] = useState<InvestmentHolding[]>(starterBook().investments);
  const [invoices, setInvoices] = useState<Invoice[]>(starterBook().invoices);
  const [lastSavedAt, setLastSavedAt] = useState<string>('');
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveFileName, setSaveFileName] = useState('MapleMoney-Book.mmoney');
  const [documentsPath, setDocumentsPath] = useState('C:\\');
  const [cloudBooks, setCloudBooks] = useState<CloudBookSummary[]>([]);
  const [selectedRegisterAccount, setSelectedRegisterAccount] = useState('all');
  const [showCloudPanel, setShowCloudPanel] = useState(false);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveToCloud, setSaveToCloud] = useState(false);

  const [accountForm, setAccountForm] = useState({
    name: '',
    type: 'checking' as AccountType,
    institution: '',
    balance: '',
    notes: '',
  });
  const [transactionForm, setTransactionForm] = useState({
    date: today(),
    type: 'payment' as TransactionType,
    payee: '',
    memo: '',
    category: 'Food & Dining',
    accountId: 'acct-checking',
    transferAccountId: '',
    amount: '',
    cleared: false,
  });
  const [billForm, setBillForm] = useState({
    payee: '',
    amount: '',
    dueDate: addFrequency(today(), 'monthly'),
    frequency: 'monthly' as Frequency,
    category: 'Utilities',
    accountId: 'acct-checking',
    reminder: true,
    autoPay: false,
  });
  const [budgetForm, setBudgetForm] = useState({
    group: 'Committed Expenses',
    category: '',
    planned: '',
  });
  const [investmentForm, setInvestmentForm] = useState({
    accountId: 'acct-watch',
    symbol: '',
    name: '',
    shares: '',
    costBasis: '',
    currentPrice: '',
  });
  const [invoiceForm, setInvoiceForm] = useState({
    customer: '',
    issueDate: today(),
    dueDate: addFrequency(today(), 'monthly'),
    accountId: 'acct-checking',
    description: '',
    quantity: '1',
    rate: '',
    notes: '',
  });

  const accountMap = useMemo(
    () => Object.fromEntries(accounts.map((account) => [account.id, account])),
    [accounts],
  );

  const categoryTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    transactions
      .filter((transaction) => transaction.type === 'payment')
      .forEach((transaction) => {
        totals[transaction.category] = (totals[transaction.category] || 0) + transaction.amount;
      });
    return totals;
  }, [transactions]);

  const registerTransactions = useMemo(() => {
    const filtered = selectedRegisterAccount === 'all'
      ? transactions
      : transactions.filter(
          (transaction) =>
            transaction.accountId === selectedRegisterAccount || transaction.transferAccountId === selectedRegisterAccount,
        );

    return [...filtered].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [transactions, selectedRegisterAccount]);

  const accountGroups = useMemo(
    () => ({
      assets: accounts.filter((account) => ['checking', 'savings', 'investment', 'asset', 'receivable'].includes(account.type)),
      liabilities: accounts.filter((account) => ['credit', 'loan', 'mortgage', 'liability'].includes(account.type)),
    }),
    [accounts],
  );

  const netWorth = useMemo(() => accounts.reduce((sum, account) => sum + account.balance, 0), [accounts]);
  const cashBalance = useMemo(
    () => accounts.filter((account) => ['checking', 'savings'].includes(account.type)).reduce((sum, account) => sum + account.balance, 0),
    [accounts],
  );
  const monthlyIncome = useMemo(
    () => transactions.filter((transaction) => transaction.type === 'deposit').reduce((sum, transaction) => sum + transaction.amount, 0),
    [transactions],
  );
  const monthlyExpenses = useMemo(
    () => transactions.filter((transaction) => transaction.type === 'payment').reduce((sum, transaction) => sum + transaction.amount, 0),
    [transactions],
  );
  const investmentValue = useMemo(
    () => investments.reduce((sum, holding) => sum + holding.shares * holding.currentPrice, 0),
    [investments],
  );
  const investmentGain = useMemo(
    () => investments.reduce((sum, holding) => sum + holding.shares * (holding.currentPrice - holding.costBasis), 0),
    [investments],
  );
  const receivablesBalance = useMemo(
    () => invoices.filter((invoice) => invoice.status !== 'paid').reduce((sum, invoice) => sum + getInvoiceTotal(invoice), 0),
    [invoices],
  );

  const dashboardCards = [
    { label: 'Net Worth', value: formatCurrency(netWorth), detail: 'Assets minus liabilities', icon: TrendingUp },
    { label: 'Cash Balance', value: formatCurrency(cashBalance), detail: 'Checking and savings', icon: Wallet },
    { label: 'Accounts Receivable', value: formatCurrency(receivablesBalance), detail: 'Open customer invoices', icon: Receipt },
    { label: 'Portfolio', value: formatCurrency(investmentValue), detail: `${investmentGain >= 0 ? '+' : ''}${formatCurrency(investmentGain)} gain/loss`, icon: BarChart3 },
  ];

  const topNav = [
    { label: 'Home', section: 'overview' as SectionId },
    { label: 'Banking', section: 'register' as SectionId },
    { label: 'Bills', section: 'bills' as SectionId },
    { label: 'Reports', section: 'reports' as SectionId },
    { label: 'Budget', section: 'budget' as SectionId },
    { label: 'Investing', section: 'investing' as SectionId },
    { label: 'Business', section: 'business' as SectionId },
  ];

  const sidebarItems = [
    { id: 'overview' as SectionId, label: 'My Money', icon: Home },
    { id: 'accounts' as SectionId, label: 'Account List', icon: Landmark },
    { id: 'register' as SectionId, label: 'Account Register', icon: ArrowLeftRight },
    { id: 'bills' as SectionId, label: 'Bills Summary', icon: Bell },
    { id: 'budget' as SectionId, label: 'Budget Summary', icon: FileSpreadsheet },
    { id: 'investing' as SectionId, label: 'Investments', icon: TrendingUp },
    { id: 'business' as SectionId, label: 'Invoice List', icon: Building2 },
    { id: 'reports' as SectionId, label: 'Reports Home', icon: BarChart3 },
  ];

  const applyBook = (book: MoneyBookData, nextFilePath?: string) => {
    setBookName(book.bookName || 'MapleMoney Book');
    setSaveMode(book.saveMode || 'local');
    setLocalFilePath(nextFilePath || book.localFilePath || '');
    setCloudBookId(book.cloudBookId || '');
    setAccounts(book.accounts || []);
    setTransactions(book.transactions || []);
    setBills(book.bills || []);
    setBudgetItems(book.budgetItems || []);
    setInvestments(book.investments || []);
    setInvoices(book.invoices || []);
    setSaveFileName(`${sanitizeFileName(book.bookName || 'MapleMoney-Book')}.mmoney`);
  };

  const buildBook = (overrides?: Partial<MoneyBookData>): MoneyBookData => ({
    version: 1,
    bookName,
    saveMode,
    localFilePath,
    cloudBookId,
    accounts,
    transactions,
    bills,
    budgetItems,
    investments,
    invoices,
    ...overrides,
  });

  const getDefaultSavePath = async () => {
    const docs = await getDocumentsPath();
    const folder = `${docs}\\MapleMoney`;
    await ensureDir(folder);
    return `${folder}\\${sanitizeFileName(bookName)}.mmoney`;
  };

  const resetBook = () => {
    const fresh = starterBook();
    applyBook(fresh);
    setLastSavedAt('');
    setActiveSection('overview');
    toast({
      title: 'New book created',
      description: 'Loaded a fresh MapleMoney workspace.',
    });
  };

  const refreshCloudBooks = async () => {
    if (!user) {
      setCloudBooks([]);
      return;
    }

    const db = supabase as any;
    const { data, error } = await db
      .from('maple_money_books')
      .select('id, book_name, updated_at')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });

    if (error) {
      toast({
        title: 'Cloud books unavailable',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }

    setCloudBooks((data || []) as CloudBookSummary[]);
  };

  const saveLocalBook = async (targetPath?: string) => {
    setIsSaving(true);
    try {
      const path = targetPath || localFilePath || (await getDefaultSavePath());
      const folder = path.slice(0, path.lastIndexOf('\\'));
      const payload = buildBook({ saveMode: 'local', localFilePath: path });
      await ensureDir(folder);
      await globalVFS.save(path, JSON.stringify(payload, null, 2));
      window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: folder } }));
      setSaveMode('local');
      setLocalFilePath(path);
      setLastSavedAt(new Date().toLocaleString());
      setSaveFileName(path.split('\\').pop() || `${sanitizeFileName(bookName)}.mmoney`);
      toast({
        title: 'Saved locally',
        description: path,
      });
      return true;
    } catch {
      toast({
        title: 'Local save failed',
        description: 'MapleMoney could not save this book to the VFS.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const saveCloudBook = async ({ setAsPrimary = true }: { setAsPrimary?: boolean } = {}) => {
    if (!user) {
      toast({
        title: 'Sign in required',
        description: 'Cloud save is available when you are signed in.',
        variant: 'destructive',
      });
      return false;
    }

    setIsSaving(true);
    try {
      const db = supabase as any;
      const payload = buildBook({ saveMode: 'cloud', cloudBookId });
      const { data, error } = await db
        .from('maple_money_books')
        .upsert(
          {
            id: cloudBookId || undefined,
            user_id: user.id,
            book_name: bookName,
            book_data: payload,
            last_opened_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,book_name' },
        )
        .select('id')
        .single();

      if (error) throw error;

      setCloudBookId(data?.id || cloudBookId);
      if (setAsPrimary) {
        setSaveMode('cloud');
      }
      setLastSavedAt(new Date().toLocaleString());
      await refreshCloudBooks();
      toast({
        title: 'Saved to cloud',
        description: `${bookName} is now stored in your MapleMoney cloud vault.`,
      });
      return true;
    } catch (error: any) {
      toast({
        title: 'Cloud save failed',
        description: error?.message || 'Unable to save this MapleMoney book.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const saveBook = async (targetPath?: string) => {
    const savedLocally = await saveLocalBook(targetPath);
    if (!savedLocally || !saveToCloud) {
      return savedLocally;
    }

    await saveCloudBook({ setAsPrimary: false });
    return true;
  };

  const handleSaveAction = async () => {
    if (localFilePath) {
      await saveBook();
      return;
    }

    setShowSaveDialog(true);
  };

  const showQuickHelp = () => {
    toast({
      title: 'MapleMoney help',
      description: 'Use File to open or save books, the left navigation to move between money areas, and Cloud books in View when you want to browse synced books.',
    });
  };

  const showAboutMapleMoney = () => {
    setShowAboutDialog(true);
  };

  const openLocalBook = async (file: FileSystemItem) => {
    try {
      const content = file.content || (await globalVFS.load(file.path));
      if (!content) throw new Error('File is empty.');
      const parsed = parseBook(content);
      applyBook(parsed, file.path);
      setLastSavedAt(new Date().toLocaleString());
      setSaveMode('local');
      toast({
        title: 'Book loaded',
        description: file.name,
      });
    } catch (error: any) {
      toast({
        title: 'Open failed',
        description: error?.message || 'This file is not a valid MapleMoney book.',
        variant: 'destructive',
      });
    }
  };

  const openCloudBook = async (id: string) => {
    if (!user) return;
    try {
      const db = supabase as any;
      const { data, error } = await db
        .from('maple_money_books')
        .select('id, book_name, book_data')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      const book = typeof data.book_data === 'string' ? parseBook(data.book_data) : (data.book_data as MoneyBookData);
      applyBook({ ...book, cloudBookId: data.id, bookName: data.book_name });
      setCloudBookId(data.id);
      setSaveMode('cloud');
      setLastSavedAt(new Date().toLocaleString());
      toast({
        title: 'Cloud book opened',
        description: data.book_name,
      });
    } catch (error: any) {
      toast({
        title: 'Cloud open failed',
        description: error?.message || 'Unable to open this cloud book.',
        variant: 'destructive',
      });
    }
  };

  const updateAccountBalances = (transaction: MoneyTransaction) => {
    setAccounts((currentAccounts) =>
      currentAccounts.map((account) => {
        if (transaction.type === 'transfer') {
          if (account.id === transaction.accountId) {
            return { ...account, balance: account.balance - transaction.amount };
          }
          if (account.id === transaction.transferAccountId) {
            return { ...account, balance: account.balance + transaction.amount };
          }
          return account;
        }

        if (account.id !== transaction.accountId) return account;
        const delta = transaction.type === 'deposit' ? transaction.amount : -transaction.amount;
        return { ...account, balance: account.balance + delta };
      }),
    );
  };

  const addTransaction = () => {
    if (!transactionForm.accountId || !transactionForm.payee.trim() || !transactionForm.amount) {
      toast({
        title: 'Transaction incomplete',
        description: 'Enter an account, payee, and amount before posting to the register.',
        variant: 'destructive',
      });
      return;
    }

    if (transactionForm.type === 'transfer' && !transactionForm.transferAccountId) {
      toast({
        title: 'Transfer account required',
        description: 'Choose the destination account for this transfer.',
        variant: 'destructive',
      });
      return;
    }

    const transaction: MoneyTransaction = {
      id: createId(),
      date: transactionForm.date,
      type: transactionForm.type,
      payee: transactionForm.payee.trim(),
      memo: transactionForm.memo.trim(),
      category: transactionForm.type === 'transfer' ? 'Transfer' : transactionForm.category,
      accountId: transactionForm.accountId,
      transferAccountId: transactionForm.transferAccountId || undefined,
      amount: Number(transactionForm.amount),
      cleared: transactionForm.cleared,
    };

    setTransactions((current) => [transaction, ...current]);
    updateAccountBalances(transaction);
    setTransactionForm({
      date: today(),
      type: 'payment',
      payee: '',
      memo: '',
      category: 'Food & Dining',
      accountId: transactionForm.accountId,
      transferAccountId: '',
      amount: '',
      cleared: false,
    });
    toast({
      title: 'Register updated',
      description: `${transaction.payee} posted to ${accountMap[transaction.accountId]?.name || 'account'}.`,
    });
  };

  const addAccount = () => {
    if (!accountForm.name.trim()) {
      toast({
        title: 'Account name required',
        description: 'Give the new account a name.',
        variant: 'destructive',
      });
      return;
    }

    const account: Account = {
      id: createId(),
      name: accountForm.name.trim(),
      type: accountForm.type,
      institution: accountForm.institution.trim(),
      balance: Number(accountForm.balance || 0),
      notes: accountForm.notes.trim(),
    };

    setAccounts((current) => [...current, account]);
    setAccountForm({ name: '', type: 'checking', institution: '', balance: '', notes: '' });
    toast({
      title: 'Account added',
      description: `${account.name} is now part of this MapleMoney book.`,
    });
  };

  const addBill = () => {
    if (!billForm.payee.trim() || !billForm.amount || !billForm.accountId) {
      toast({
        title: 'Bill incomplete',
        description: 'Enter the payee, amount, and payment account.',
        variant: 'destructive',
      });
      return;
    }

    const bill: Bill = {
      id: createId(),
      payee: billForm.payee.trim(),
      amount: Number(billForm.amount),
      dueDate: billForm.dueDate,
      frequency: billForm.frequency,
      category: billForm.category,
      accountId: billForm.accountId,
      reminder: billForm.reminder,
      autoPay: billForm.autoPay,
      active: true,
    };

    setBills((current) => [...current, bill]);
    setBillForm({
      payee: '',
      amount: '',
      dueDate: addFrequency(today(), 'monthly'),
      frequency: 'monthly',
      category: 'Utilities',
      accountId: billForm.accountId,
      reminder: true,
      autoPay: false,
    });
    toast({
      title: 'Bill scheduled',
      description: `${bill.payee} will appear in your bill calendar.`,
    });
  };

  const payBill = (bill: Bill) => {
    const payment: MoneyTransaction = {
      id: createId(),
      date: today(),
      type: 'payment',
      payee: bill.payee,
      memo: 'Bill payment',
      category: bill.category,
      accountId: bill.accountId,
      amount: bill.amount,
      cleared: true,
    };

    setTransactions((current) => [payment, ...current]);
    updateAccountBalances(payment);
    setBills((current) =>
      current.map((entry) =>
        entry.id === bill.id
          ? {
              ...entry,
              lastPaidDate: today(),
              dueDate: addFrequency(entry.dueDate, entry.frequency),
              active: entry.autoPay || entry.frequency !== 'yearly' ? true : entry.active,
            }
          : entry,
      ),
    );
    toast({
      title: 'Bill paid',
      description: `${bill.payee} was recorded in the register.`,
    });
  };

  const addBudgetItem = () => {
    if (!budgetForm.category.trim() || !budgetForm.planned) {
      toast({
        title: 'Budget incomplete',
        description: 'Enter a category and planned amount.',
        variant: 'destructive',
      });
      return;
    }

    setBudgetItems((current) => [
      ...current,
      {
        id: createId(),
        group: budgetForm.group,
        category: budgetForm.category.trim(),
        planned: Number(budgetForm.planned),
      },
    ]);
    setBudgetForm({ group: 'Committed Expenses', category: '', planned: '' });
    toast({
      title: 'Budget line added',
      description: 'The budget has been updated.',
    });
  };

  const addInvestment = () => {
    if (!investmentForm.symbol.trim() || !investmentForm.shares || !investmentForm.currentPrice || !investmentForm.accountId) {
      toast({
        title: 'Investment incomplete',
        description: 'Enter the symbol, quantity, price, and account.',
        variant: 'destructive',
      });
      return;
    }

    setInvestments((current) => [
      ...current,
      {
        id: createId(),
        accountId: investmentForm.accountId,
        symbol: investmentForm.symbol.trim().toUpperCase(),
        name: investmentForm.name.trim() || investmentForm.symbol.trim().toUpperCase(),
        shares: Number(investmentForm.shares),
        costBasis: Number(investmentForm.costBasis || 0),
        currentPrice: Number(investmentForm.currentPrice),
      },
    ]);
    setInvestmentForm({
      accountId: investmentForm.accountId,
      symbol: '',
      name: '',
      shares: '',
      costBasis: '',
      currentPrice: '',
    });
    toast({
      title: 'Holding added',
      description: 'The investment watchlist has been updated.',
    });
  };

  const addInvoice = () => {
    if (!invoiceForm.customer.trim() || !invoiceForm.description.trim() || !invoiceForm.rate || !invoiceForm.accountId) {
      toast({
        title: 'Invoice incomplete',
        description: 'Enter the customer, line item, rate, and deposit account.',
        variant: 'destructive',
      });
      return;
    }

    const invoice: Invoice = {
      id: createId(),
      customer: invoiceForm.customer.trim(),
      issueDate: invoiceForm.issueDate,
      dueDate: invoiceForm.dueDate,
      accountId: invoiceForm.accountId,
      status: 'sent',
      notes: invoiceForm.notes.trim(),
      items: [
        {
          description: invoiceForm.description.trim(),
          quantity: Number(invoiceForm.quantity || 1),
          rate: Number(invoiceForm.rate),
        },
      ],
    };

    setInvoices((current) => [invoice, ...current]);
    setInvoiceForm({
      customer: '',
      issueDate: today(),
      dueDate: addFrequency(today(), 'monthly'),
      accountId: invoiceForm.accountId,
      description: '',
      quantity: '1',
      rate: '',
      notes: '',
    });
    toast({
      title: 'Invoice created',
      description: `${invoice.customer} has been added to Accounts Receivable.`,
    });
  };

  const receiveInvoicePayment = (invoice: Invoice) => {
    if (invoice.status === 'paid') return;

    const receipt: MoneyTransaction = {
      id: createId(),
      date: today(),
      type: 'deposit',
      payee: invoice.customer,
      memo: 'Invoice payment received',
      category: 'Accounts Receivable',
      accountId: invoice.accountId,
      amount: getInvoiceTotal(invoice),
      cleared: true,
    };

    setTransactions((current) => [receipt, ...current]);
    updateAccountBalances(receipt);
    setInvoices((current) =>
      current.map((entry) =>
        entry.id === invoice.id ? { ...entry, status: 'paid', paymentDate: today() } : entry,
      ),
    );
    toast({
      title: 'Payment received',
      description: `${invoice.customer}'s invoice was posted as income.`,
    });
  };

  const toggleCleared = (transactionId: string) => {
    setTransactions((current) =>
      current.map((transaction) =>
        transaction.id === transactionId ? { ...transaction, cleared: !transaction.cleared } : transaction,
      ),
    );
  };

  useEffect(() => {
    getDocumentsPath()
      .then((path) => setDocumentsPath(`${path}\\MapleMoney`))
      .catch(() => setDocumentsPath('C:\\User\\Default\\Documents\\MapleMoney'));
  }, []);

  useEffect(() => {
    refreshCloudBooks();
  }, [user]);

  useEffect(() => {
    if (!initialContent) {
      if (filePath) setLocalFilePath(filePath);
      return;
    }

    try {
      const parsed = parseBook(initialContent);
      applyBook(parsed, filePath);
      setLastSavedAt(new Date().toLocaleString());
    } catch {
      toast({
        title: 'Could not open file',
        description: 'The selected MapleMoney file could not be read.',
        variant: 'destructive',
      });
    }
  }, [initialContent, filePath]);

  const renderOverview = () => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {dashboardCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.label} className={panelClass}>
              <CardHeader className={`${panelHeaderClass} flex flex-row items-center justify-between`}>
                <CardTitle className={`text-sm font-semibold ${headingClass}`}>{card.label}</CardTitle>
                <Icon className={`h-4 w-4 ${headingClass}`} />
              </CardHeader>
              <CardContent className="pt-4">
                <div className={`text-2xl font-bold ${accentClass}`}>{card.value}</div>
                <p className={`mt-1 text-xs ${mutedClass}`}>{card.detail}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.3fr_1fr]">
        <Card className={panelClass}>
          <CardHeader className={panelHeaderClass}>
            <CardTitle className={`text-base ${headingClass}`}>Favorite accounts</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow className={tableRowClass}>
                  <TableHead className={mutedClass}>Account</TableHead>
                  <TableHead className={mutedClass}>Institution</TableHead>
                  <TableHead className={`text-right ${mutedClass}`}>Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.slice(0, 5).map((account) => (
                  <TableRow key={account.id} className={tableRowClass}>
                    <TableCell className={`font-medium ${accentClass}`}>{account.name}</TableCell>
                    <TableCell className={accentClass}>{account.institution || '—'}</TableCell>
                    <TableCell className={`text-right ${accentClass}`}>{formatCurrency(account.balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className={panelClass}>
            <CardHeader className={panelHeaderClass}>
              <CardTitle className={`text-base ${headingClass}`}>Reminders</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              {bills
                .filter((bill) => bill.active)
                .slice(0, 3)
                .map((bill) => (
                  <div key={bill.id} className={`${surfaceSoftClass} p-3`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className={`font-medium ${accentClass}`}>{bill.payee}</div>
                        <div className={`text-xs ${mutedClass}`}>Due {formatDate(bill.dueDate)}</div>
                      </div>
                      <Badge
                        variant={daysUntil(bill.dueDate) <= 7 ? 'destructive' : 'outline'}
                        className={daysUntil(bill.dueDate) > 7 ? badgeClass : ''}
                      >
                        {daysUntil(bill.dueDate) <= 0 ? 'Due now' : `${daysUntil(bill.dueDate)} days`}
                      </Badge>
                    </div>
                  </div>
                ))}
            </CardContent>
          </Card>

          <Card className={panelClass}>
            <CardHeader className={panelHeaderClass}>
              <CardTitle className={`text-base ${headingClass}`}>Storage</CardTitle>
            </CardHeader>
            <CardContent className={`space-y-3 pt-4 text-sm ${mutedClass}`}>
              <div className="flex items-center justify-between">
                <span>Current mode</span>
                <Badge variant="outline" className={badgeClass}>{saveMode === 'cloud' ? 'Cloud book' : 'Local VFS book'}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span>Local path</span>
                <span className="max-w-[60%] truncate text-right text-xs">{localFilePath || 'Not saved yet'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Cloud status</span>
                <span className="text-xs">{user ? `${cloudBooks.length} cloud books` : 'Sign in to enable'}</span>
              </div>
              <div className="rounded-md border px-3 py-2 text-xs text-muted-foreground">
                Use <span className="font-medium text-foreground">File</span> → <span className="font-medium text-foreground">Save</span> or <span className="font-medium text-foreground">Save As</span> to choose where this book is stored.
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );

  const renderAccounts = () => (
    <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
      <Card className={panelClass}>
        <CardHeader className={panelHeaderClass}>
          <CardTitle className={`text-base ${headingClass}`}>Account list</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow className={tableRowClass}>
                <TableHead className={mutedClass}>Account</TableHead>
                <TableHead className={mutedClass}>Type</TableHead>
                <TableHead className={mutedClass}>Institution</TableHead>
                <TableHead className={`text-right ${mutedClass}`}>Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id} className={tableRowClass}>
                  <TableCell className={`font-medium ${accentClass}`}>{account.name}</TableCell>
                  <TableCell className={`capitalize ${accentClass}`}>{account.type}</TableCell>
                  <TableCell className={accentClass}>{account.institution || '—'}</TableCell>
                  <TableCell className={`text-right ${account.balance < 0 ? negativeClass : accentClass}`}>
                    {formatCurrency(account.balance)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className={panelClass}>
        <CardHeader className={panelHeaderClass}>
          <CardTitle className={`text-base ${headingClass}`}>Add account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-4">
          <div>
            <Label className={accentClass}>Name</Label>
            <Input className={inputClass} value={accountForm.name} onChange={(event) => setAccountForm((current) => ({ ...current, name: event.target.value }))} />
          </div>
          <div>
            <Label className={accentClass}>Type</Label>
            <Select value={accountForm.type} onValueChange={(value: AccountType) => setAccountForm((current) => ({ ...current, type: value }))}>
              <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
              <SelectContent className={selectContentClass}>
                {['checking', 'savings', 'credit', 'investment', 'loan', 'mortgage', 'asset', 'liability', 'receivable'].map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className={accentClass}>Institution</Label>
            <Input className={inputClass} value={accountForm.institution} onChange={(event) => setAccountForm((current) => ({ ...current, institution: event.target.value }))} />
          </div>
          <div>
            <Label className={accentClass}>Opening balance</Label>
            <Input className={inputClass} type="number" step="0.01" value={accountForm.balance} onChange={(event) => setAccountForm((current) => ({ ...current, balance: event.target.value }))} />
          </div>
          <div>
            <Label className={accentClass}>Notes</Label>
            <Textarea className={inputClass} rows={3} value={accountForm.notes} onChange={(event) => setAccountForm((current) => ({ ...current, notes: event.target.value }))} />
          </div>
          <Button className={`w-full ${primaryButtonClass}`} onClick={addAccount}>
            <Plus className="mr-2 h-4 w-4" />
            Add account
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const renderRegister = () => (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
        <Card className={panelClass}>
          <CardHeader className={panelHeaderClass}>
            <CardTitle className={`text-base ${headingClass}`}>Enter a transaction</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className={accentClass}>Date</Label>
                <Input className={inputClass} type="date" value={transactionForm.date} onChange={(event) => setTransactionForm((current) => ({ ...current, date: event.target.value }))} />
              </div>
              <div>
                <Label className={accentClass}>Type</Label>
                <Select value={transactionForm.type} onValueChange={(value: TransactionType) => setTransactionForm((current) => ({ ...current, type: value }))}>
                  <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                  <SelectContent className={selectContentClass}>
                    <SelectItem value="deposit">Deposit</SelectItem>
                    <SelectItem value="payment">Payment</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className={accentClass}>Account</Label>
                <Select value={transactionForm.accountId} onValueChange={(value) => setTransactionForm((current) => ({ ...current, accountId: value }))}>
                  <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                  <SelectContent className={selectContentClass}>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {transactionForm.type === 'transfer' ? (
                <div>
                  <Label className={accentClass}>To account</Label>
                  <Select value={transactionForm.transferAccountId} onValueChange={(value) => setTransactionForm((current) => ({ ...current, transferAccountId: value }))}>
                    <SelectTrigger className={inputClass}><SelectValue placeholder="Choose account" /></SelectTrigger>
                    <SelectContent className={selectContentClass}>
                      {accounts
                        .filter((account) => account.id !== transactionForm.accountId)
                        .map((account) => (
                          <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label className={accentClass}>Category</Label>
                  <Input className={inputClass} value={transactionForm.category} onChange={(event) => setTransactionForm((current) => ({ ...current, category: event.target.value }))} />
                </div>
              )}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className={accentClass}>Payee / memo</Label>
                <Input className={inputClass} value={transactionForm.payee} onChange={(event) => setTransactionForm((current) => ({ ...current, payee: event.target.value }))} />
              </div>
              <div>
                <Label className={accentClass}>Amount</Label>
                <Input className={inputClass} type="number" step="0.01" value={transactionForm.amount} onChange={(event) => setTransactionForm((current) => ({ ...current, amount: event.target.value }))} />
              </div>
            </div>
            <div>
              <Label className={accentClass}>Notes</Label>
              <Textarea className={inputClass} rows={3} value={transactionForm.memo} onChange={(event) => setTransactionForm((current) => ({ ...current, memo: event.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={transactionForm.cleared} onCheckedChange={(checked) => setTransactionForm((current) => ({ ...current, cleared: checked }))} />
              <span className={`text-sm ${mutedClass}`}>Mark as cleared</span>
            </div>
            <Button className={`w-full ${primaryButtonClass}`} onClick={addTransaction}>
              <Plus className="mr-2 h-4 w-4" />
              Post transaction
            </Button>
          </CardContent>
        </Card>

        <Card className={panelClass}>
          <CardHeader className={`${panelHeaderClass} flex flex-col gap-2 md:flex-row md:items-center md:justify-between`}>
            <CardTitle className={`text-base ${headingClass}`}>Account register</CardTitle>
            <Select value={selectedRegisterAccount} onValueChange={setSelectedRegisterAccount}>
              <SelectTrigger className={`w-full md:w-[220px] ${inputClass}`}><SelectValue /></SelectTrigger>
              <SelectContent className={selectContentClass}>
                <SelectItem value="all">All accounts</SelectItem>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="pt-4">
            <div className={`max-h-[420px] overflow-auto ${surfaceClass}`}>
              <Table>
                <TableHeader>
                  <TableRow className={tableRowClass}>
                    <TableHead className={mutedClass}>Date</TableHead>
                    <TableHead className={mutedClass}>Payee / Memo</TableHead>
                    <TableHead className={mutedClass}>Account</TableHead>
                    <TableHead className={mutedClass}>Category</TableHead>
                    <TableHead className={`text-right ${mutedClass}`}>Amount</TableHead>
                    <TableHead className={`text-right ${mutedClass}`}>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {registerTransactions.map((transaction) => (
                    <TableRow key={transaction.id} className={tableRowClass}>
                      <TableCell className={accentClass}>{formatDate(transaction.date)}</TableCell>
                      <TableCell>
                        <div className={`font-medium ${accentClass}`}>{transaction.payee}</div>
                        <div className={`text-xs ${mutedClass}`}>{transaction.memo || '—'}</div>
                      </TableCell>
                      <TableCell className={accentClass}>{accountMap[transaction.accountId]?.name || '—'}</TableCell>
                      <TableCell className={accentClass}>{transaction.category}</TableCell>
                      <TableCell className={`text-right ${transaction.type === 'deposit' ? positiveClass : transaction.type === 'payment' ? negativeClass : headingClass}`}>
                        {transaction.type === 'deposit' ? '+' : transaction.type === 'payment' ? '-' : '↔'}{formatCurrency(transaction.amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" className={inputClass} onClick={() => toggleCleared(transaction.id)}>
                          {transaction.cleared ? 'Cleared' : 'Pending'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderBills = () => (
    <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
      <Card className={panelClass}>
        <CardHeader className={panelHeaderClass}>
          <CardTitle className={`text-base ${headingClass}`}>Bills summary</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow className={tableRowClass}>
                <TableHead className={mutedClass}>Payee</TableHead>
                <TableHead className={mutedClass}>Due date</TableHead>
                <TableHead className={mutedClass}>Frequency</TableHead>
                <TableHead className={`text-right ${mutedClass}`}>Amount</TableHead>
                <TableHead className={`text-right ${mutedClass}`}>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bills.filter((bill) => bill.active).map((bill) => (
                <TableRow key={bill.id} className={tableRowClass}>
                  <TableCell>
                    <div className={`font-medium ${accentClass}`}>{bill.payee}</div>
                    <div className={`text-xs ${mutedClass}`}>{bill.category}</div>
                  </TableCell>
                  <TableCell className={accentClass}>{formatDate(bill.dueDate)}</TableCell>
                  <TableCell className={`capitalize ${accentClass}`}>{bill.frequency}</TableCell>
                  <TableCell className={`text-right ${accentClass}`}>{formatCurrency(bill.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" className={primaryButtonClass} onClick={() => payBill(bill)}>
                      Pay
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className={panelClass}>
        <CardHeader className={panelHeaderClass}>
          <CardTitle className={`text-base ${headingClass}`}>Schedule a bill</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-4">
          <div>
            <Label className={accentClass}>Payee</Label>
            <Input className={inputClass} value={billForm.payee} onChange={(event) => setBillForm((current) => ({ ...current, payee: event.target.value }))} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label className={accentClass}>Amount</Label>
              <Input className={inputClass} type="number" step="0.01" value={billForm.amount} onChange={(event) => setBillForm((current) => ({ ...current, amount: event.target.value }))} />
            </div>
            <div>
              <Label className={accentClass}>Due date</Label>
              <Input className={inputClass} type="date" value={billForm.dueDate} onChange={(event) => setBillForm((current) => ({ ...current, dueDate: event.target.value }))} />
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label className={accentClass}>Frequency</Label>
              <Select value={billForm.frequency} onValueChange={(value: Frequency) => setBillForm((current) => ({ ...current, frequency: value }))}>
                <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                <SelectContent className={selectContentClass}>
                  {['weekly', 'monthly', 'quarterly', 'yearly'].map((frequency) => (
                    <SelectItem key={frequency} value={frequency}>{frequency}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={accentClass}>Category</Label>
              <Input className={inputClass} value={billForm.category} onChange={(event) => setBillForm((current) => ({ ...current, category: event.target.value }))} />
            </div>
          </div>
          <div>
            <Label className={accentClass}>Pay from account</Label>
            <Select value={billForm.accountId} onValueChange={(value) => setBillForm((current) => ({ ...current, accountId: value }))}>
              <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
              <SelectContent className={selectContentClass}>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className={`flex items-center justify-between ${surfaceClass} p-3`}>
            <div>
              <div className={`text-sm font-medium ${accentClass}`}>Reminder</div>
              <div className={`text-xs ${mutedClass}`}>Show this bill in reminders.</div>
            </div>
            <Switch checked={billForm.reminder} onCheckedChange={(checked) => setBillForm((current) => ({ ...current, reminder: checked }))} />
          </div>
          <div className={`flex items-center justify-between ${surfaceClass} p-3`}>
            <div>
              <div className={`text-sm font-medium ${accentClass}`}>Auto-pay label</div>
              <div className={`text-xs ${mutedClass}`}>Keep this bill recurring after payment.</div>
            </div>
            <Switch checked={billForm.autoPay} onCheckedChange={(checked) => setBillForm((current) => ({ ...current, autoPay: checked }))} />
          </div>
          <Button className={`w-full ${primaryButtonClass}`} onClick={addBill}>
            <Plus className="mr-2 h-4 w-4" />
            Add bill
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const renderBudget = () => {
    const totalPlanned = budgetItems.reduce((sum, item) => sum + item.planned, 0);
    const totalActual = budgetItems.reduce((sum, item) => sum + (categoryTotals[item.category] || 0), 0);

    return (
      <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <Card className={panelClass}>
          <CardHeader className={panelHeaderClass}>
            <CardTitle className={`text-base ${headingClass}`}>Working budget: current month</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow className={tableRowClass}>
                  <TableHead className={mutedClass}>Budget group</TableHead>
                  <TableHead className={mutedClass}>Category</TableHead>
                  <TableHead className={`text-right ${mutedClass}`}>Planned</TableHead>
                  <TableHead className={`text-right ${mutedClass}`}>Actual</TableHead>
                  <TableHead className={`text-right ${mutedClass}`}>Difference</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {budgetItems.map((item) => {
                  const actual = categoryTotals[item.category] || 0;
                  const difference = item.planned - actual;
                  return (
                    <TableRow key={item.id} className={tableRowClass}>
                      <TableCell className={accentClass}>{item.group}</TableCell>
                      <TableCell className={`font-medium ${accentClass}`}>{item.category}</TableCell>
                      <TableCell className={`text-right ${accentClass}`}>{formatCurrency(item.planned)}</TableCell>
                      <TableCell className={`text-right ${accentClass}`}>{formatCurrency(actual)}</TableCell>
                      <TableCell className={`text-right ${difference >= 0 ? positiveClass : negativeClass}`}>
                        {formatCurrency(difference)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className={tableRowClass}>
                  <TableCell colSpan={2} className={`font-semibold ${accentClass}`}>Totals</TableCell>
                  <TableCell className={`text-right font-semibold ${accentClass}`}>{formatCurrency(totalPlanned)}</TableCell>
                  <TableCell className={`text-right font-semibold ${accentClass}`}>{formatCurrency(totalActual)}</TableCell>
                  <TableCell className={`text-right font-semibold ${totalPlanned - totalActual >= 0 ? positiveClass : negativeClass}`}>
                    {formatCurrency(totalPlanned - totalActual)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className={panelClass}>
          <CardHeader className={panelHeaderClass}>
            <CardTitle className={`text-base ${headingClass}`}>Add budget category</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-4">
            <div>
              <Label className={accentClass}>Group</Label>
              <Select value={budgetForm.group} onValueChange={(value) => setBudgetForm((current) => ({ ...current, group: value }))}>
                <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                <SelectContent className={selectContentClass}>
                  {['Income', 'Committed Expenses', 'Fun', 'Irregular Expenses', 'Savings & Debt', 'Retirement'].map((group) => (
                    <SelectItem key={group} value={group}>{group}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={accentClass}>Category</Label>
              <Input className={inputClass} value={budgetForm.category} onChange={(event) => setBudgetForm((current) => ({ ...current, category: event.target.value }))} />
            </div>
            <div>
              <Label className={accentClass}>Planned amount</Label>
              <Input className={inputClass} type="number" step="0.01" value={budgetForm.planned} onChange={(event) => setBudgetForm((current) => ({ ...current, planned: event.target.value }))} />
            </div>
            <Button className={`w-full ${primaryButtonClass}`} onClick={addBudgetItem}>
              <Plus className="mr-2 h-4 w-4" />
              Add budget line
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderInvesting = () => (
    <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
      <Card className={panelClass}>
        <CardHeader className={panelHeaderClass}>
          <CardTitle className={`text-base ${headingClass}`}>Portfolio manager</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className={`${surfaceClass} p-3`}>
              <div className={`text-xs uppercase tracking-wide ${mutedClass}`}>Market value</div>
              <div className={`mt-1 text-xl font-bold ${accentClass}`}>{formatCurrency(investmentValue)}</div>
            </div>
            <div className={`${surfaceClass} p-3`}>
              <div className={`text-xs uppercase tracking-wide ${mutedClass}`}>Gain / loss</div>
              <div className={`mt-1 text-xl font-bold ${investmentGain >= 0 ? positiveClass : negativeClass}`}>
                {investmentGain >= 0 ? '+' : ''}{formatCurrency(investmentGain)}
              </div>
            </div>
            <div className={`${surfaceClass} p-3`}>
              <div className={`text-xs uppercase tracking-wide ${mutedClass}`}>Holdings</div>
              <div className={`mt-1 text-xl font-bold ${accentClass}`}>{investments.length}</div>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow className={tableRowClass}>
                <TableHead className={mutedClass}>Symbol</TableHead>
                <TableHead className={mutedClass}>Name</TableHead>
                <TableHead className={`text-right ${mutedClass}`}>Shares</TableHead>
                <TableHead className={`text-right ${mutedClass}`}>Price</TableHead>
                <TableHead className={`text-right ${mutedClass}`}>Value</TableHead>
                <TableHead className={`text-right ${mutedClass}`}>Gain / loss</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {investments.map((holding) => {
                const value = holding.shares * holding.currentPrice;
                const gain = holding.shares * (holding.currentPrice - holding.costBasis);
                return (
                  <TableRow key={holding.id} className={tableRowClass}>
                    <TableCell className={`font-medium ${accentClass}`}>{holding.symbol}</TableCell>
                    <TableCell className={accentClass}>{holding.name}</TableCell>
                    <TableCell className={`text-right ${accentClass}`}>{holding.shares}</TableCell>
                    <TableCell className={`text-right ${accentClass}`}>{formatCurrency(holding.currentPrice)}</TableCell>
                    <TableCell className={`text-right ${accentClass}`}>{formatCurrency(value)}</TableCell>
                    <TableCell className={`text-right ${gain >= 0 ? positiveClass : negativeClass}`}>
                      {formatCurrency(gain)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className={panelClass}>
        <CardHeader className={panelHeaderClass}>
          <CardTitle className={`text-base ${headingClass}`}>Add holding</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-4">
          <div>
            <Label className={accentClass}>Investment account</Label>
            <Select value={investmentForm.accountId} onValueChange={(value) => setInvestmentForm((current) => ({ ...current, accountId: value }))}>
              <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
              <SelectContent className={selectContentClass}>
                {accounts.filter((account) => account.type === 'investment').map((account) => (
                  <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label className={accentClass}>Symbol</Label>
              <Input className={inputClass} value={investmentForm.symbol} onChange={(event) => setInvestmentForm((current) => ({ ...current, symbol: event.target.value }))} />
            </div>
            <div>
              <Label className={accentClass}>Name</Label>
              <Input className={inputClass} value={investmentForm.name} onChange={(event) => setInvestmentForm((current) => ({ ...current, name: event.target.value }))} />
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <Label className={accentClass}>Shares</Label>
              <Input className={inputClass} type="number" step="0.01" value={investmentForm.shares} onChange={(event) => setInvestmentForm((current) => ({ ...current, shares: event.target.value }))} />
            </div>
            <div>
              <Label className={accentClass}>Cost basis</Label>
              <Input className={inputClass} type="number" step="0.01" value={investmentForm.costBasis} onChange={(event) => setInvestmentForm((current) => ({ ...current, costBasis: event.target.value }))} />
            </div>
            <div>
              <Label className={accentClass}>Current price</Label>
              <Input className={inputClass} type="number" step="0.01" value={investmentForm.currentPrice} onChange={(event) => setInvestmentForm((current) => ({ ...current, currentPrice: event.target.value }))} />
            </div>
          </div>
          <Button className={`w-full ${primaryButtonClass}`} onClick={addInvestment}>
            <Plus className="mr-2 h-4 w-4" />
            Add holding
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const renderBusiness = () => (
    <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
      <Card className={panelClass}>
        <CardHeader className={panelHeaderClass}>
          <CardTitle className={`text-base ${headingClass}`}>Accounts receivable</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow className={tableRowClass}>
                <TableHead className={mutedClass}>Customer</TableHead>
                <TableHead className={mutedClass}>Invoice date</TableHead>
                <TableHead className={mutedClass}>Due date</TableHead>
                <TableHead className={mutedClass}>Status</TableHead>
                <TableHead className={`text-right ${mutedClass}`}>Amount</TableHead>
                <TableHead className={`text-right ${mutedClass}`}>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id} className={tableRowClass}>
                  <TableCell>
                    <div className={`font-medium ${accentClass}`}>{invoice.customer}</div>
                    <div className={`text-xs ${mutedClass}`}>{invoice.items[0]?.description || 'Invoice item'}</div>
                  </TableCell>
                  <TableCell className={accentClass}>{formatDate(invoice.issueDate)}</TableCell>
                  <TableCell className={accentClass}>{formatDate(invoice.dueDate)}</TableCell>
                  <TableCell>
                    <Badge variant={invoice.status === 'paid' ? 'default' : 'outline'} className={invoice.status === 'paid' ? paidBadgeClass : badgeClass}>
                      {invoice.status}
                    </Badge>
                  </TableCell>
                  <TableCell className={`text-right ${accentClass}`}>{formatCurrency(getInvoiceTotal(invoice))}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className={inputClass}
                      disabled={invoice.status === 'paid'}
                      onClick={() => receiveInvoicePayment(invoice)}
                    >
                      Receive payment
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className={panelClass}>
        <CardHeader className={panelHeaderClass}>
          <CardTitle className={`text-base ${headingClass}`}>New invoice</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-4">
          <div>
            <Label className={accentClass}>Customer</Label>
            <Input className={inputClass} value={invoiceForm.customer} onChange={(event) => setInvoiceForm((current) => ({ ...current, customer: event.target.value }))} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label className={accentClass}>Invoice date</Label>
              <Input className={inputClass} type="date" value={invoiceForm.issueDate} onChange={(event) => setInvoiceForm((current) => ({ ...current, issueDate: event.target.value }))} />
            </div>
            <div>
              <Label className={accentClass}>Due date</Label>
              <Input className={inputClass} type="date" value={invoiceForm.dueDate} onChange={(event) => setInvoiceForm((current) => ({ ...current, dueDate: event.target.value }))} />
            </div>
          </div>
          <div>
            <Label className={accentClass}>Deposit to account</Label>
            <Select value={invoiceForm.accountId} onValueChange={(value) => setInvoiceForm((current) => ({ ...current, accountId: value }))}>
              <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
              <SelectContent className={selectContentClass}>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className={accentClass}>Line item</Label>
            <Input className={inputClass} value={invoiceForm.description} onChange={(event) => setInvoiceForm((current) => ({ ...current, description: event.target.value }))} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label className={accentClass}>Quantity</Label>
              <Input className={inputClass} type="number" min="1" step="1" value={invoiceForm.quantity} onChange={(event) => setInvoiceForm((current) => ({ ...current, quantity: event.target.value }))} />
            </div>
            <div>
              <Label className={accentClass}>Rate</Label>
              <Input className={inputClass} type="number" step="0.01" value={invoiceForm.rate} onChange={(event) => setInvoiceForm((current) => ({ ...current, rate: event.target.value }))} />
            </div>
          </div>
          <div>
            <Label className={accentClass}>Notes</Label>
            <Textarea className={inputClass} rows={3} value={invoiceForm.notes} onChange={(event) => setInvoiceForm((current) => ({ ...current, notes: event.target.value }))} />
          </div>
          <Button className={`w-full ${primaryButtonClass}`} onClick={addInvoice}>
            <Plus className="mr-2 h-4 w-4" />
            Create invoice
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const renderReports = () => {
    const spendingCategories = Object.entries(categoryTotals)
      .sort(([, left], [, right]) => right - left)
      .slice(0, 6);

    return (
      <div className="grid gap-4 xl:grid-cols-2">
        <Card className={panelClass}>
          <CardHeader className={panelHeaderClass}>
            <CardTitle className={`text-base ${headingClass}`}>Net worth summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-4 text-sm">
            <div className={`flex items-center justify-between ${surfaceClass} p-3`}>
              <span className={mutedClass}>Total assets</span>
              <span className={`font-semibold ${accentClass}`}>
                {formatCurrency(accountGroups.assets.reduce((sum, account) => sum + account.balance, 0))}
              </span>
            </div>
            <div className={`flex items-center justify-between ${surfaceClass} p-3`}>
              <span className={mutedClass}>Total liabilities</span>
              <span className={`font-semibold ${negativeClass}`}>
                {formatCurrency(Math.abs(accountGroups.liabilities.reduce((sum, account) => sum + account.balance, 0)))}
              </span>
            </div>
            <div className={`flex items-center justify-between ${surfaceClass} p-3`}>
              <span className={mutedClass}>Net worth</span>
              <span className={`font-semibold ${accentClass}`}>{formatCurrency(netWorth)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className={panelClass}>
          <CardHeader className={panelHeaderClass}>
            <CardTitle className={`text-base ${headingClass}`}>Cash flow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-4 text-sm">
            <div className={`flex items-center justify-between ${surfaceClass} p-3`}>
              <span className={mutedClass}>Income posted</span>
              <span className={`font-semibold ${positiveClass}`}>{formatCurrency(monthlyIncome)}</span>
            </div>
            <div className={`flex items-center justify-between ${surfaceClass} p-3`}>
              <span className={mutedClass}>Expenses posted</span>
              <span className={`font-semibold ${negativeClass}`}>{formatCurrency(monthlyExpenses)}</span>
            </div>
            <div className={`flex items-center justify-between ${surfaceClass} p-3`}>
              <span className={mutedClass}>Net cash flow</span>
              <span className={`font-semibold ${accentClass}`}>{formatCurrency(monthlyIncome - monthlyExpenses)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className={panelClass}>
          <CardHeader className={panelHeaderClass}>
            <CardTitle className={`text-base ${headingClass}`}>Spending by category</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-4">
            {spendingCategories.length === 0 ? (
              <div className={emptyStateClass}>
                No spending has been posted yet.
              </div>
            ) : (
              spendingCategories.map(([category, total]) => (
                <div key={category} className={`${surfaceClass} p-3`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className={`font-medium ${accentClass}`}>{category}</span>
                    <span className={accentClass}>{formatCurrency(total)}</span>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className={panelClass}>
          <CardHeader className={panelHeaderClass}>
            <CardTitle className={`text-base ${headingClass}`}>Upcoming items</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-4 text-sm">
            {bills
              .filter((bill) => bill.active)
              .sort((a, b) => (a.dueDate > b.dueDate ? 1 : -1))
              .slice(0, 4)
              .map((bill) => (
                <div key={bill.id} className={`flex items-center justify-between ${surfaceClass} p-3`}>
                  <div>
                    <div className={`font-medium ${accentClass}`}>{bill.payee}</div>
                    <div className={`text-xs ${mutedClass}`}>Due {formatDate(bill.dueDate)}</div>
                  </div>
                  <div className={`font-semibold ${accentClass}`}>{formatCurrency(bill.amount)}</div>
                </div>
              ))}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'accounts':
        return renderAccounts();
      case 'register':
        return renderRegister();
      case 'bills':
        return renderBills();
      case 'budget':
        return renderBudget();
      case 'investing':
        return renderInvesting();
      case 'business':
        return renderBusiness();
      case 'reports':
        return renderReports();
      case 'overview':
      default:
        return renderOverview();
    }
  };

  return (
    <div className={appRootClass}>
      <div className={topBarClass}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <DollarSign className="h-5 w-5" />
            <span className="font-semibold">MapleMoney</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold">My Money</span>
            <span className="text-white/80">/</span>
            <Input
              value={bookName}
              onChange={(event) => {
                const nextName = event.target.value;
                setBookName(nextName);
                setSaveFileName(`${sanitizeFileName(nextName)}.mmoney`);
              }}
              className={topInputClass}
              placeholder="Book name"
            />
            <Badge className={topBadgeClass}>
              {saveMode === 'cloud' ? 'Cloud' : 'Local VFS'}
            </Badge>
          </div>
        </div>
      </div>

      <div className="border-b border-border bg-background">
        <Menubar className="h-8 rounded-none border-none bg-transparent">
          <MenubarMenu>
            <MenubarTrigger className="px-3 py-1 h-8">File</MenubarTrigger>
            <MenubarContent className="z-[10000]">
              <MenubarItem onClick={resetBook}>
                <RefreshCcw className="mr-2 h-4 w-4" />
                New
              </MenubarItem>
              <MenubarItem onClick={() => setShowOpenDialog(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Open local...
              </MenubarItem>
              <MenubarSeparator />
              <MenubarItem onClick={handleSaveAction} disabled={isSaving}>
                <Save className="mr-2 h-4 w-4" />
                Save
              </MenubarItem>
              <MenubarItem onClick={() => setShowSaveDialog(true)} disabled={isSaving}>
                <FileText className="mr-2 h-4 w-4" />
                Save As...
              </MenubarItem>
            </MenubarContent>
          </MenubarMenu>

          <MenubarMenu>
            <MenubarTrigger className="px-3 py-1 h-8">View</MenubarTrigger>
            <MenubarContent className="z-[10000]">
              <MenubarItem onClick={() => setShowCloudPanel((current) => !current)}>
                <Cloud className="mr-2 h-4 w-4" />
                {showCloudPanel ? 'Hide' : 'Show'} Cloud Books
              </MenubarItem>
              <MenubarItem
                onClick={() => {
                  setShowCloudPanel(true);
                  refreshCloudBooks();
                }}
                disabled={!user}
              >
                <RefreshCcw className="mr-2 h-4 w-4" />
                Refresh Cloud Books
              </MenubarItem>
            </MenubarContent>
          </MenubarMenu>

          <MenubarMenu>
            <MenubarTrigger className="px-3 py-1 h-8">Help</MenubarTrigger>
            <MenubarContent className="z-[10000]">
              <MenubarItem onClick={showQuickHelp}>
                <CircleHelp className="mr-2 h-4 w-4" />
                MapleMoney Help
              </MenubarItem>
              <MenubarItem onClick={showAboutMapleMoney}>
                <Info className="mr-2 h-4 w-4" />
                About MapleMoney
              </MenubarItem>
            </MenubarContent>
          </MenubarMenu>
        </Menubar>
      </div>

      <div className={secondBarClass}>
        <div className="flex flex-wrap items-center gap-5">
          {topNav.map((item) => (
            <button
              key={item.label}
              type="button"
              className={`transition hover:text-white ${activeSection === item.section ? 'font-semibold text-white' : 'text-white/80'}`}
              onClick={() => setActiveSection(item.section)}
            >
              {item.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 text-xs text-white/80">
            <Calendar className="h-4 w-4" />
            {lastSavedAt ? `Last saved ${lastSavedAt}` : 'Not saved yet'}
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside className={sidebarClass}>
          <div className={sidebarHeaderClass}>
            <div className={`text-lg font-semibold ${titleClass}`}>MapleMoney</div>
            <div className={`text-xs ${mutedClass}`}>Microsoft Money-inspired personal finance workspace</div>
          </div>
          <div className="px-3 py-3">
            <div className={`mb-2 text-xs font-semibold uppercase tracking-wide ${mutedClass}`}>Navigation</div>
            <div className="space-y-1">
              {sidebarItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveSection(item.id)}
                    className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm transition ${isActive ? activeNavItemClass : inactiveNavItemClass}`}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
          <Separator className={sidebarSeparatorClass} />
          <div className={`space-y-3 px-3 py-3 text-sm ${mutedClass}`}>
            <div>
              <div className={`mb-1 text-xs font-semibold uppercase tracking-wide ${mutedClass}`}>Storage</div>
              <div className={`${surfaceSoftClass} p-3`}>
                <div className="flex items-center justify-between">
                  <span>Local book</span>
                  <Badge variant="outline" className={badgeClass}>{localFilePath ? 'Ready' : 'Unsaved'}</Badge>
                </div>
                <div className="mt-2 text-xs">{localFilePath ? localFilePath.split('\\').pop() : saveFileName}</div>
              </div>
            </div>

            <div className={`${surfaceSoftClass} p-3`}>
              <div className="mb-2 flex items-center justify-between">
                <span>Cloud books</span>
                <Button size="sm" variant="outline" className={`h-7 px-2 text-xs ${inputClass}`} onClick={() => setShowCloudPanel((current) => !current)}>
                  {showCloudPanel ? 'Hide' : 'Show'}
                </Button>
              </div>
              <div className="text-xs">{user ? `${cloudBooks.length} available in Maple cloud` : 'Sign in for cloud sync'}</div>
            </div>
          </div>
        </aside>

        <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {showCloudPanel && (
            <div className={cloudPanelClass}>
              <div className="mb-2 flex items-center justify-between">
                <div className={`text-sm font-semibold ${headingClass}`}>Cloud books</div>
                <Button size="sm" variant="outline" className={inputClass} onClick={refreshCloudBooks}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Refresh
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {cloudBooks.length === 0 ? (
                  <span className={`text-sm ${mutedClass}`}>No cloud books yet.</span>
                ) : (
                  cloudBooks.map((book) => (
                    <Button key={book.id} size="sm" variant="outline" className={inputClass} onClick={() => openCloudBook(book.id)}>
                      {book.book_name}
                    </Button>
                  ))
                )}
              </div>
            </div>
          )}

          <ScrollArea className="h-full">
            <div className="space-y-4 p-4">{renderContent()}</div>
          </ScrollArea>
        </main>
      </div>

      <DraggableFileDialog
        isOpen={showOpenDialog}
        onClose={() => setShowOpenDialog(false)}
        onFileSelect={openLocalBook}
        mode="open"
        initialPath={documentsPath}
        acceptedTypes={['application/json', 'text/plain']}
        title="Open MapleMoney Book"
      />
      <DraggableFileDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        mode="save"
        initialPath={documentsPath}
        title="Save MapleMoney Book"
        fileName={saveFileName}
        onFileNameChange={setSaveFileName}
        saveOptions={
          <div className="flex items-start gap-3 rounded-md border px-3 py-2">
            <Checkbox
              id="maple-money-cloud-save"
              checked={saveToCloud}
              onCheckedChange={(checked) => setSaveToCloud(checked === true)}
              disabled={!user}
              className="mt-0.5"
            />
            <label htmlFor="maple-money-cloud-save" className="cursor-pointer space-y-1 text-sm">
              <div className="font-medium text-foreground">Also save a cloud copy</div>
              <div className="text-xs text-muted-foreground">
                {user ? 'Save still writes locally first. Turn this on to sync the same book to your MapleMoney cloud vault too.' : 'Sign in to enable cloud save.'}
              </div>
            </label>
          </div>
        }
        onSave={async (folder) => {
          const base = folder.path.endsWith('\\') ? folder.path.slice(0, -1) : folder.path;
          const fileName = saveFileName.toLowerCase().endsWith('.mmoney') ? saveFileName : `${saveFileName}.mmoney`;
          return await saveBook(`${base}\\${fileName}`);
        }}
      />

      <Dialog open={showAboutDialog} onOpenChange={setShowAboutDialog}>
        <DialogContent className="z-[9999] overflow-hidden sm:max-w-[520px]">
          <div className="space-y-6">
            <div className="flex items-center gap-4 border-b border-border pb-4">
              <MapleMoneyIcon size={64} className="h-16 w-16 shrink-0" />
              <div>
                <h2 className="text-2xl font-bold">MapleMoney</h2>
                <p className="text-sm text-muted-foreground">Personal Finance Manager</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="font-medium">Version:</span>
                <span className="font-mono text-sm">1.0.0</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Edition:</span>
                <span className="text-sm">MapleOS Desktop</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Book format:</span>
                <span className="font-mono text-sm">.mmoney</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Cloud sync:</span>
                <span className="text-sm">Optional</span>
              </div>
            </div>

            <div className="space-y-3 border-t border-border pt-4">
              <h3 className="flex items-center gap-2 font-semibold">
                <Monitor className="h-4 w-4" />
                System Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Platform:</span>
                  <span className="font-mono">WASM x64</span>
                </div>
                <div className="flex justify-between">
                  <span>CPU Cores:</span>
                  <span className="font-mono">{navigator.hardwareConcurrency || 'Unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Device Memory:</span>
                  <span className="font-mono">{(navigator as any).deviceMemory ? `${(navigator as any).deviceMemory}GB` : 'Unknown'}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3 border-t border-border pt-4">
              <h3 className="font-semibold">Included Features</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <Wallet className="h-3 w-3" />
                  <span>Account registers</span>
                </div>
                <div className="flex items-center gap-2">
                  <Receipt className="h-3 w-3" />
                  <span>Bill tracking</span>
                </div>
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-3 w-3" />
                  <span>Budget planning</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-3 w-3" />
                  <span>Investments</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="h-3 w-3" />
                  <span>Business tools</span>
                </div>
                <div className="flex items-center gap-2">
                  <Cloud className="h-3 w-3" />
                  <span>Cloud copy support</span>
                </div>
              </div>
            </div>

            <div className="space-y-2 border-t border-border pt-4 text-center text-sm">
              <p className="font-medium">© 2026 MapleOS</p>
              <p className="text-muted-foreground">Inspired by classic Windows personal finance software.</p>
            </div>

            <div className="flex justify-end border-t border-border pt-4">
              <Button onClick={() => setShowAboutDialog(false)} size="sm">
                OK
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};